import { createAsyncThunk } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';

import type { Channel } from '../../../models';
import { FuelEntityType, FuelPortalService } from '../../../services';
import { IngestService } from '../../../services/ingest-service';
import type { RootState } from '..';
import { SetSnackbarMessage } from '../application-data';
import { UpdateForm } from '../channel-editor';
import { SaveAssetJob } from './SaveAssetJob';
import type { StartJobPayload } from './SaveAssetJobsState';
import { removeJob, updateJob } from './SaveAssetJobsState';
import { uploadFile } from './uploadFile';

export const processSaveChannelThumbnailJob = createAsyncThunk(
  'saveAssetJobs/processSaveChannelThumbnailJob',
  async (payload: StartJobPayload, { dispatch, getState }) => {
    const { form, jobType, extraParams } = payload;
    const state = getState() as RootState;
    const job = new SaveAssetJob(form, jobType, extraParams);
    const { schemas } = state.applicationData;
    const { permissions } = state.fuelUserAccess;
    const { ouData } = state.applicationData;
    // console.log('processSaveChannelThumbnailJob', { job, schemas, categories });
    const { id } = form;
    let uploadUrls = null;

    const updateMessage = (message: string) => {
      // console.log('message', message);
      job.message = message;
      dispatch(updateJob(job));
    };

    const getUrl = (uploadUrls: any) => {
      const url = '';
      if (jobType === 'Subtitle') {
        return uploadUrls.externalSubtitleS3UploadUrl;
      }
      if (jobType === 'ChannelThumbnail') {
        return uploadUrls.entityUploadUrl;
      }
      return url;
    };

    const getExtension = (str) => str.substr(str.lastIndexOf('.') + 1);

    const getExtensionText = () => {
      if (jobType === 'Subtitle') {
        return 'subtitleext';
      }
      if (jobType === 'Thumbnail' || jobType === 'ChannelThumbnail') {
        return 'thumbnailext';
      }
      return null;
    };
    let extension = null;
    const extensionText = getExtensionText();
    const file = job.channelForm.ingestFile;
    if (file) {
      extension = getExtension(file.name);
    }
    job.progress = 0;
    try {
      updateMessage(`Preparing for upload...`);
      uploadUrls = await IngestService.getUploadUrl(id, FuelEntityType.Channel, extensionText, extension);
      updateMessage(`Uploading file...`);
      const url = getUrl(uploadUrls);
      await uploadFile(file, url, (progress) => {
        job.progress = progress;
      });
    } catch (err) {
      updateMessage(`There was an error uploading file.`);
      throw err;
    }

    let channel = await FuelPortalService.getEntityById(FuelEntityType.Channel, id, { fullInfo: true });
    // console.log('uploadUrls', uploadUrls)
    if (channel && channel.metaData && uploadUrls && jobType === 'ChannelThumbnail') {
      // asset.data.externalSubtitles = externalSubtitles;
      channel.metaData.thumbnail = {
        s3Path: uploadUrls.entityUploadPath,
        publicUrl: null,
        previewUri: null,
      };
      updateMessage(`Updating channel...`);
      // Updating asset with allowRestricted true for only thumbnail
      channel = await FuelPortalService.upsertEntity(FuelEntityType.Channel, channel as Channel);
      const newChannelForm = cloneDeep(form);
      if (channel && channel.metaData && channel.metaData.thumbnail && channel.metaData.thumbnail.previewUri) {
        newChannelForm.thumbnailSrc = channel.metaData.thumbnail.previewUri;
      }
      dispatch(UpdateForm(newChannelForm));
    }

    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    dispatch(
      SetSnackbarMessage({
        message: 'File successfully uploaded.',
        severity: 'success',
      })
    );
    return channel;
  }
);
